// ===== World 9: Pizza factory – GEARS AND CONVEYOR BELTS =====
// A turning gear turns the next one the opposite way. A big wheel drives a small wheel fast.
// A gear on the belt's axle drives the conveyor belt – and the belt's direction decides where the tomato goes.
// Ball → goal: tomato → pizza box (or the oven).
//
// Mechanics (our own, no Matter bodies for gears – they sit on the wall behind the level):
//  - axles: motor (pizza_gear motor:±1), fixed gears (pizza_gear), pegs (pizza_pegs), belt/roller axles.
//  - the player's gears (pizza_wheel / pizza_big_wheel) snap onto the nearest free peg.
//  - two gears mesh when their distance = r1 + r2 (± TOL). Rotation spreads out from the motor:
//    w2 = -w1 * r1 / r2. Overlap or conflicting directions (a ring with an odd count) = jammed.
//  - belts and rollers move the balls that touch them (tangential speed).
import { Registry, defineWorld, definePart, defineObj } from '../../core/registry';
import type { Ctx2D, Gfx, GameBody, Level, LevelObj, PartDef, Placement, PhysicsApi, Reskin, SimState, CorePartType, SynthStyle, Vec, WorldDef, WorldMeta, SoundFn } from '../../core/types';
import { scene } from './scene';
import { levels } from './levels';
import { strings } from './strings';
import { K, NS, NB, RS, RB, RP, at, type Pt, type PegsObj, type GearObj, type BeltObj, type RollerObj } from './shared';

// ---------- constants ----------
const INK = '#3B2A5C';
const TOL = 9, SNAP = 105;               // mesh tolerance, peg magnet
const W0 = 0.07;                         // motor's angular speed (rad/frame)
const BELTK = 2.6, PINK = 1.5;           // belt / roller speed per (w * r)
const COL: Record<string, [string, string]> = { s: ['#FFE68A', '#F2A516'], b: ['#D9C9FA', '#9A83DB'], m: ['#FF9C84', '#E0394A'], f: ['#E4E0EE', '#A29BB8'], c: ['#A8E6FF', '#3E9BD6'] };
const GSPEC: Record<string, { n: number; R: number }> = { s: { n: NS, R: RS }, b: { n: NB, R: RB } };

// ---------- axles and the gear network ----------
type Size = 's' | 'b';
interface Gear { n: number; R: number; col: string }
interface Axle {
  x: number; y: number; gears: Gear[]; w: number; phase: number; jam: boolean; motor: number; isPeg: boolean; taken: boolean;
  speed?: number; phase0?: number; pegIdx?: number | null; placed?: PlacedGear;
}
/** A gear the player placed (and the axle it snapped onto) */
interface PlacedGear { pl: Placement; idx: number; size: Size; axle: Axle | null; x: number; y: number }
interface Edge { a: Axle; b: Axle; ga?: Gear; gb?: Gear; d?: number; clash?: boolean; link?: boolean }
interface BeltState { body: GameBody; axle: Axle; axle2: Axle | null; pos: number; vb: number }
interface RollerState { body: GameBody; axle: Axle; r: number; ang: number }
interface Net { axles: Axle[]; placed: PlacedGear[]; links: [Axle, Axle][]; belts: BeltState[]; pins: RollerState[]; solved: boolean }
declare module '../../core/types' { interface SimState { pz?: Net } }

function zz(api: PhysicsApi): Net { return api.S.pz || (api.S.pz = { axles: [], placed: [], links: [], belts: [], pins: [], solved: false }); }
function gearsOf(spec: string | undefined, col?: string): Gear[] { return (spec || '').split('').filter(c => GSPEC[c]).map(c => ({ n: GSPEC[c].n, R: GSPEC[c].R, col: col || (c === 's' ? 's' : 'b') })); }
function addAxle(Z: Net, a: Partial<Axle> & { x: number; y: number }): Axle { const ax: Axle = Object.assign({ gears: [], w: 0, phase: 0, jam: false, motor: 0, isPeg: false, taken: false }, a); Z.axles.push(ax); return ax; }

// Is this object a peg? (peg rows, and belts/rollers without their own gear)
const isPegObj = (o: LevelObj) => o.t === 'pizza_pegs' || ((o.t === 'pizza_belt' || o.t === 'pizza_roller') && !o.gear);
// The level's pegs (same order everywhere): peg objects + empty belt/roller axles
function levelPegs(level: Level) {
  const out: Vec[] = [];
  for (const o of level.objs || []) {
    if (o.t === 'pizza_pegs') for (const p of (o as PegsObj).p) out.push({ x: p[0], y: p[1] });
    if (o.t === 'pizza_belt' && !o.gear) { const b = o as BeltObj; out.push({ x: b.drive === 'R' ? b.x2 : b.x1, y: b.y }); }
    if (o.t === 'pizza_roller' && !o.gear) { const r = o as RollerObj; out.push({ x: r.x, y: r.y }); }
  }
  return out;
}
// Index of object o's first peg in levelPegs(level)
function pegIndex(level: Level, o: LevelObj) {
  let idx = 0;
  for (const oo of level.objs || []) { if (oo === o) break; if (oo.t === 'pizza_pegs') idx += (oo as PegsObj).p.length; else if (isPegObj(oo)) idx++; }
  return idx;
}
// Which peg does each placement snap onto? (in order; a peg can only hold one wheel)
function snapList(pegs: Vec[], pls: (Placement | undefined)[]) {
  const taken = new Set<number>(), res: (number | null)[] = [];
  pls.forEach((pl, i) => {
    if (!pl || !Registry.parts[pl.t] || !Registry.parts[pl.t].gear) { res[i] = null; return; }
    let best = -1, bd = SNAP;
    pegs.forEach((p, j) => { if (taken.has(j)) return; const d = Math.hypot(p.x - pl.x, p.y - pl.y); if (d < bd) { bd = d; best = j; } });
    if (best >= 0) taken.add(best);
    res[i] = best >= 0 ? best : null;
  });
  return res;
}

function solve(Z: Net) {
  const A = Z.axles.filter(a => a.gears.length);
  const edges: Edge[] = [], adj = new Map<Axle, Edge[]>(A.map(a => [a, []]));
  const clash = new Set<Axle>();
  for (let i = 0; i < A.length; i++) for (let j = i + 1; j < A.length; j++) {
    const a = A[i], b = A[j], d = Math.hypot(a.x - b.x, a.y - b.y);
    let mesh: { ga: Gear; gb: Gear } | null = null, over = false;
    for (const ga of a.gears) for (const gb of b.gears) {
      if (Math.abs(d - (ga.R + gb.R)) <= TOL) { if (!mesh) mesh = { ga, gb }; }
      else if (d < ga.R + gb.R - TOL) over = true;
    }
    if (mesh) { const e = { a, b, ga: mesh.ga, gb: mesh.gb, d }; edges.push(e); adj.get(a)!.push(e); adj.get(b)!.push(e); }
    else if (over) { clash.add(a); clash.add(b); const e = { a, b, clash: true }; adj.get(a)!.push(e); adj.get(b)!.push(e); }
  }
  for (const [p, q] of Z.links || []) if (adj.has(p) && adj.has(q)) { const e = { a: p, b: q, link: true }; adj.get(p)!.push(e); adj.get(q)!.push(e); }
  const seen = new Set<Axle>();
  const order = A.filter(a => a.motor).concat(A.filter(a => !a.motor));
  for (const root of order) {
    if (seen.has(root)) continue;
    const comp = [root]; seen.add(root);
    root.w = root.motor ? root.motor * W0 * (root.speed || 1) : 0; root.phase = root.phase0 || 0;
    let bad = false;
    for (let k = 0; k < comp.length; k++) {
      const a = comp[k];
      if (a.motor && a !== root) { const want = a.motor * W0 * (a.speed || 1); if (Math.abs(want - a.w) > 1e-4) bad = true; }
      for (const e of adj.get(a)!) {
        const b = e.a === a ? e.b : e.a;
        if (e.clash) { bad = true; if (!seen.has(b)) { seen.add(b); comp.push(b); } continue; }
        if (e.link) { if (seen.has(b)) { if (Math.abs(b.w - a.w) > 1e-4) bad = true; } else { seen.add(b); comp.push(b); b.w = a.w; b.phase = b.phase0 || 0; } continue; }
        const ga = (e.a === a ? e.ga : e.gb)!, gb = (e.a === a ? e.gb : e.ga)!;
        const w = -a.w * ga.R / gb.R;
        if (seen.has(b)) { if (Math.abs(b.w - w) > 1e-4) bad = true; continue; }
        seen.add(b); comp.push(b); b.w = w;
        const th = Math.atan2(b.y - a.y, b.x - a.x), u = (th - a.phase) * ga.n / (2 * Math.PI);
        b.phase = th + Math.PI - (0.5 - u) * 2 * Math.PI / gb.n;
      }
    }
    if (bad || comp.some(a => clash.has(a))) for (const a of comp) { a.jam = true; a.w = 0; }
  }
  Z.solved = true;
}

// ---------- drawing helpers ----------
function gearPath(ctx: Ctx2D, n: number, R: number, ang: number) {
  const ro = R + 7, ri = R - 6, step = 2 * Math.PI / n;
  ctx.beginPath();
  for (let i = 0; i < n; i++) {
    const c = ang + i * step;
    const pts = [[c - step * 0.5, ri], [c - step * 0.27, ri], [c - step * 0.16, ro], [c + step * 0.16, ro], [c + step * 0.27, ri]];
    for (const [a, r] of pts) ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
  }
  ctx.closePath();
}
function drawGear(ctx: Ctx2D, g: Gfx, x: number, y: number, n: number, R: number, ang: number, col: string, opt?: { alpha?: number; noShadow?: boolean; sel?: boolean }) {
  opt = opt || {};
  const [c1, c2] = COL[col] || COL.s;
  ctx.save(); ctx.translate(x, y);
  if (opt.alpha != null) ctx.globalAlpha = opt.alpha;
  if (!opt.noShadow) g.shadowOn(ctx, 8, 4);
  gearPath(ctx, n, R, ang);
  const gr = ctx.createRadialGradient(-R * 0.3, -R * 0.35, R * 0.1, 0, 0, R + 8); gr.addColorStop(0, c1); gr.addColorStop(1, c2);
  ctx.fillStyle = gr; ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.lineJoin = 'round'; ctx.stroke();
  // inner ring + holes that turn with it (so you can see it turn)
  ctx.beginPath(); ctx.arc(0, 0, R - 12, 0, 7); ctx.strokeStyle = 'rgba(59,42,92,.25)'; ctx.lineWidth = 2; ctx.stroke();
  const nh = R > 45 ? 5 : 3, hr = R > 45 ? 9 : 6, hd = R * 0.58;
  ctx.fillStyle = 'rgba(59,42,92,.55)';
  for (let i = 0; i < nh; i++) { const a = ang + i * 2 * Math.PI / nh; ctx.beginPath(); ctx.arc(Math.cos(a) * hd, Math.sin(a) * hd, hr, 0, 7); ctx.fill(); }
  // hub
  ctx.beginPath(); ctx.arc(0, 0, 9, 0, 7); ctx.fillStyle = '#FFFFFF'; ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.beginPath(); ctx.arc(0, 0, 3, 0, 7); ctx.fillStyle = INK; ctx.fill();
  if (opt.sel) { ctx.beginPath(); ctx.arc(0, 0, R + 14, 0, 7); ctx.strokeStyle = '#fff'; ctx.lineWidth = 4; ctx.setLineDash([8, 6]); ctx.stroke(); ctx.setLineDash([]); }
  ctx.restore();
}
// Curved arrow: dir 1 = clockwise, -1 = anticlockwise
function turnArrow(ctx: Ctx2D, x: number, y: number, rad: number, dir: number, col?: string, lw?: number) {
  if (!dir) return;
  lw = lw || 6;
  ctx.save(); ctx.translate(x, y); if (dir < 0) ctx.scale(-1, 1);
  const a0 = -2.5, a1 = 0.35;
  ctx.lineCap = 'round';
  ctx.beginPath(); ctx.arc(0, 0, rad, a0, a1); ctx.strokeStyle = INK; ctx.lineWidth = lw + 5; ctx.stroke();
  ctx.beginPath(); ctx.arc(0, 0, rad, a0, a1); ctx.strokeStyle = col || '#fff'; ctx.lineWidth = lw; ctx.stroke();
  const ex = Math.cos(a1) * rad, ey = Math.sin(a1) * rad, tx = -Math.sin(a1), ty = Math.cos(a1), nx = Math.cos(a1), ny = Math.sin(a1);
  const s = lw * 1.7;
  ctx.beginPath(); ctx.moveTo(ex + tx * s * 1.3, ey + ty * s * 1.3); ctx.lineTo(ex + nx * s, ey + ny * s); ctx.lineTo(ex - nx * s, ey - ny * s); ctx.closePath();
  ctx.fillStyle = col || '#fff'; ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.lineJoin = 'round'; ctx.stroke(); ctx.fill();
  ctx.restore();
}
function axleAngle(ax: Axle, S: SimState | null | undefined) {
  const t = S && S.started ? S.t : 0;
  let a = ax.phase + ax.w * t;
  if (ax.jam && S && S.started && S.t < 80) a += Math.sin(S.t * 1.3) * 0.05 * (1 - S.t / 80);
  return a;
}
function drawAxleGears(ctx: Ctx2D, g: Gfx, ax: Axle, S: SimState) {
  const a = axleAngle(ax, S);
  // big wheels at the back
  const gs = ax.gears.slice().sort((p, q) => q.R - p.R);
  gs.forEach((ge, i) => drawGear(ctx, g, ax.x, ax.y, ge.n, ge.R, a, ge.col, { noShadow: i > 0 }));
  if (S && S.started && ax.w && !ax.motor) turnArrow(ctx, ax.x, ax.y, Math.min(24, gs[gs.length - 1].R * 0.55), Math.sign(ax.w), '#fff', 5);
  if (ax.jam && S && S.started) jamMark(ctx, ax.x, ax.y - gs[0].R - 6, S.t);
}
function jamMark(ctx: Ctx2D, x: number, y: number, t: number) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(Math.sin(t * 0.3) * 0.2);
  ctx.beginPath(); for (let i = 0; i < 10; i++) { const a = i / 10 * Math.PI * 2, r = i % 2 ? 7 : 14; ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r); }
  ctx.closePath(); ctx.fillStyle = '#FF5A5F'; ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.restore();
}
function drawPeg(ctx: Ctx2D, g: Gfx, x: number, y: number, t: number, glow: boolean) {
  ctx.save();
  if (glow) { const k = 0.5 + 0.5 * Math.sin(t * 0.06); ctx.beginPath(); ctx.arc(x, y, 20 + k * 4, 0, 7); ctx.fillStyle = `rgba(255,255,255,${0.35 + k * 0.2})`; ctx.fill(); }
  g.shadowOn(ctx, 4, 2);
  ctx.beginPath(); ctx.arc(x, y, 11, 0, 7); ctx.fillStyle = '#B8B2C8'; ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.beginPath(); ctx.arc(x - 3, y - 3, 4, 0, 7); ctx.fillStyle = '#fff'; ctx.fill();
  ctx.restore();
}
function motorBox(ctx: Ctx2D, g: Gfx, x: number, y: number, on: boolean, t: number) {
  ctx.save();
  g.block(ctx, x, y + 34, 74, 58, 0, '#FF8A70', '#D8434B', 14);
  // lightning bolt
  ctx.translate(x, y + 48);
  ctx.beginPath(); ctx.moveTo(2, -14); ctx.lineTo(-8, 2); ctx.lineTo(0, 2); ctx.lineTo(-3, 14); ctx.lineTo(8, -3); ctx.lineTo(0, -3); ctx.closePath();
  ctx.fillStyle = on ? (Math.floor(t / 8) % 2 ? '#FFF7B0' : '#FFE14D') : '#FFD35A'; ctx.fill(); ctx.lineWidth = 2.5; ctx.strokeStyle = INK; ctx.stroke();
  ctx.restore();
  // cable down
  ctx.save(); ctx.strokeStyle = INK; ctx.lineWidth = 5; ctx.lineCap = 'round';
  ctx.beginPath(); ctx.moveTo(x + 30, y + 58); ctx.quadraticCurveTo(x + 50, y + 90, x + 40, y + 120); ctx.stroke(); ctx.restore();
}

// The tomato (hero ball) and the olive (helper).
// Local face: differs from face() in draw-helpers (eyes lower and smaller, mouth lower, bigger pink cheeks).
function face(ctx: Ctx2D, r: number, v: Vec, sad: boolean | undefined) {
  const sp = Math.hypot(v.x, v.y), lx = Math.max(-2.5, Math.min(2.5, v.x * 0.5)), ly = Math.max(-2.5, Math.min(2.5, v.y * 0.5));
  const scared = v.y > 9, fast = sp > 8.5, ex = r * 0.34, ey = -r * 0.05, er = r * 0.26;
  for (const s of [-1, 1]) {
    ctx.beginPath(); ctx.arc(s * ex, ey, er, 0, 7); ctx.fillStyle = '#fff'; ctx.fill();
    ctx.beginPath(); ctx.arc(s * ex + lx * 0.6, ey + ly * 0.6, er * (scared ? 0.35 : 0.55), 0, 7); ctx.fillStyle = INK; ctx.fill();
  }
  ctx.strokeStyle = INK; ctx.fillStyle = INK; ctx.lineWidth = 2; ctx.lineCap = 'round';
  if (sad) { ctx.beginPath(); ctx.arc(0, r * 0.65, r * 0.26, 1.2 * Math.PI, 1.8 * Math.PI); ctx.stroke(); }
  else if (fast || scared) { ctx.beginPath(); ctx.ellipse(0, r * 0.45, r * 0.16, r * 0.2, 0, 0, 7); ctx.fill(); }
  else { ctx.beginPath(); ctx.arc(0, r * 0.3, r * 0.26, 0.15 * Math.PI, 0.85 * Math.PI); ctx.stroke(); }
  ctx.fillStyle = 'rgba(255,190,200,.7)';
  [-1, 1].forEach(s => { ctx.beginPath(); ctx.ellipse(s * r * 0.6, r * 0.28, r * 0.14, r * 0.09, 0, 0, 7); ctx.fill(); });
}
function tomato(ctx: Ctx2D, g: Gfx, x: number, y: number, r: number, roll: number | undefined, v: Vec, sad: boolean | undefined, helper?: boolean) {
  ctx.save(); ctx.translate(x, y);
  g.shadowOn(ctx, 8, 5);
  const gr = ctx.createRadialGradient(-r * .35, -r * .4, r * .12, 0, 0, r * 1.05);
  if (helper) { gr.addColorStop(0, '#9FA66A'); gr.addColorStop(0.5, '#5E6B2E'); gr.addColorStop(1, '#3A4318'); }
  else { gr.addColorStop(0, '#FFB0A0'); gr.addColorStop(0.5, '#F2463C'); gr.addColorStop(1, '#B92430'); }
  ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.fillStyle = gr; ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 2; ctx.strokeStyle = 'rgba(59,42,92,.7)'; ctx.stroke();
  ctx.save(); ctx.rotate(roll || 0);
  if (!helper) { // green star on top
    ctx.beginPath();
    for (let i = 0; i < 10; i++) { const a = -Math.PI / 2 + i * Math.PI / 5, rr = i % 2 ? r * 0.18 : r * 0.5; ctx.lineTo(Math.cos(a) * rr, -r * 0.82 + Math.sin(a) * rr * 0.55); }
    ctx.closePath(); ctx.fillStyle = '#5CC36B'; ctx.fill(); ctx.lineWidth = 1.5; ctx.strokeStyle = '#2E7D3A'; ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0, -r * 0.85); ctx.lineTo(1, -r * 1.12); ctx.strokeStyle = '#2E7D3A'; ctx.lineWidth = 3; ctx.lineCap = 'round'; ctx.stroke();
  } else { ctx.beginPath(); ctx.ellipse(0, -r * 0.1, r * 0.3, r * 0.22, 0, 0, 7); ctx.fillStyle = '#FF7A4A'; ctx.fill(); }
  if (!helper) face(ctx, r, v, sad);
  else { ctx.fillStyle = '#fff'; [-1, 1].forEach(s => { ctx.beginPath(); ctx.arc(s * r * 0.36, r * 0.25, r * 0.16, 0, 7); ctx.fill(); ctx.beginPath(); ctx.arc(s * r * 0.36, r * 0.27, r * 0.08, 0, 7); ctx.fillStyle = INK; ctx.fill(); ctx.fillStyle = '#fff'; }); }
  ctx.restore();
  ctx.beginPath(); ctx.ellipse(-r * .38, -r * .42, r * .26, r * .16, -0.6, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.6)'; ctx.fill();
  ctx.restore();
}

// Small pizza (box logo and contents); n = tomatoes on top
function pizzaTop(ctx: Ctx2D, x: number, y: number, rx: number, ry: number, n: number) {
  ctx.save();
  ctx.beginPath(); ctx.ellipse(x, y, rx, ry, 0, 0, 7); ctx.fillStyle = '#F2B45A'; ctx.fill(); ctx.lineWidth = 2.5; ctx.strokeStyle = INK; ctx.stroke();
  ctx.beginPath(); ctx.ellipse(x, y, rx * 0.8, ry * 0.78, 0, 0, 7); ctx.fillStyle = '#E8563F'; ctx.fill();
  ctx.fillStyle = '#FFE08A';
  [[-0.4, -0.2, .22], [0.3, -0.3, .2], [0.1, 0.3, .25], [-0.2, 0.35, .15], [0.5, 0.15, .15]].forEach(([a, b, s]) => { ctx.beginPath(); ctx.ellipse(x + a * rx, y + b * ry, rx * s, ry * s, 0, 0, 7); ctx.fill(); });
  for (let i = 0; i < (n || 0); i++) { const a = i * 2.4 + 0.5, d = i ? 0.45 : 0; ctx.beginPath(); ctx.ellipse(x + Math.cos(a) * rx * d, y + Math.sin(a) * ry * d, rx * 0.16, ry * 0.3, 0, 0, 7); ctx.fillStyle = '#C8323B'; ctx.fill(); ctx.lineWidth = 2; ctx.strokeStyle = INK; ctx.stroke(); }
  ctx.restore();
}

// Draw a belt (pos/vb from the belt's state, or made up in the intro)
function drawBelt(ctx: Ctx2D, g: Gfx, x1: number, x2: number, y: number, pos: number, vb: number) {
  const L = x2 - x1;
  ctx.save();
  // the belt itself
  g.shadowOn(ctx, 10, 5);
  g.rr(ctx, x1 - RP - 3, y - RP - 3, L + 2 * RP + 6, 2 * RP + 6, RP + 3); ctx.fillStyle = '#5E5773'; ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  // side plate (metal)
  g.rr(ctx, x1, y - RP + 7, L, 2 * RP - 14, 6); ctx.fillStyle = '#B8B2C8'; ctx.fill(); ctx.lineWidth = 2; ctx.strokeStyle = 'rgba(59,42,92,.6)'; ctx.stroke();
  // top belt with moving stripes/arrows
  ctx.save();
  ctx.beginPath(); ctx.rect(x1 - 2, y - RP - 4, L + 4, 10); ctx.clip();
  ctx.fillStyle = '#4A4460'; ctx.fillRect(x1 - 2, y - RP - 4, L + 4, 10);
  const sp = 30, off = ((pos % sp) + sp) % sp;
  ctx.strokeStyle = '#FFE08A'; ctx.lineWidth = 3; ctx.lineCap = 'round';
  const d = Math.abs(vb) > 0.05 ? Math.sign(vb) : 0;
  for (let x = x1 - sp + off; x < x2 + sp; x += sp) {
    ctx.beginPath();
    if (d) { ctx.moveTo(x - 4 * d, y - RP - 3); ctx.lineTo(x + 3 * d, y - RP + 1); ctx.lineTo(x - 4 * d, y - RP + 5); }
    else { ctx.moveTo(x, y - RP - 3); ctx.lineTo(x, y - RP + 5); }
    ctx.stroke();
  }
  ctx.restore();
  // rollers at the ends (turn with the belt)
  for (const px of [x1, x2]) {
    ctx.beginPath(); ctx.arc(px, y, RP - 4, 0, 7); ctx.fillStyle = '#DCD8E8'; ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
    const a = pos / (RP - 4);
    ctx.strokeStyle = INK; ctx.lineWidth = 3;
    for (let i = 0; i < 3; i++) { const b = a + i * 2.094; ctx.beginPath(); ctx.moveTo(px, y); ctx.lineTo(px + Math.cos(b) * (RP - 7), y + Math.sin(b) * (RP - 7)); ctx.stroke(); }
    ctx.beginPath(); ctx.arc(px, y, 4, 0, 7); ctx.fillStyle = INK; ctx.fill();
  }
  ctx.restore();
}
function drawRoller(ctx: Ctx2D, g: Gfx, x: number, y: number, r: number, ang: number) {
  ctx.save(); ctx.translate(x, y);
  g.shadowOn(ctx, 10, 5);
  ctx.beginPath(); ctx.arc(0, 0, r, 0, 7);
  const gr = ctx.createRadialGradient(-r * 0.3, -r * 0.35, r * 0.1, 0, 0, r); gr.addColorStop(0, '#FFE3B0'); gr.addColorStop(1, '#D9954E');
  ctx.fillStyle = gr; ctx.fill(); g.shadowOff(ctx); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.beginPath(); ctx.arc(0, 0, r * 0.62, 0, 7); ctx.strokeStyle = 'rgba(150,80,30,.45)'; ctx.lineWidth = 2.5; ctx.stroke();
  ctx.beginPath(); ctx.arc(0, 0, r * 0.3, 0, 7); ctx.stroke();
  // flour spots and a stripe that turn
  ctx.rotate(ang);
  ctx.fillStyle = 'rgba(255,255,255,.85)';
  [[0.7, 0], [0.75, 2.1], [0.72, 4.2]].forEach(([d, a]) => { ctx.beginPath(); ctx.arc(Math.cos(a) * r * d, Math.sin(a) * r * d, 4.5, 0, 7); ctx.fill(); });
  ctx.strokeStyle = '#B86A2C'; ctx.lineWidth = 4; ctx.lineCap = 'round';
  ctx.beginPath(); ctx.moveTo(r * 0.3, 0); ctx.lineTo(r - 4, 0); ctx.stroke();
  ctx.beginPath(); ctx.arc(0, 0, 7, 0, 7); ctx.fillStyle = '#fff'; ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.restore();
}

// Pizza peel (the plank): wide blade + handle
function peel(ctx: Ctx2D, g: Gfx, w: number, h: number, sel: boolean) {
  const bw = w * 0.66, bx0 = -w / 2, bh = h + 10;
  g.shadowOn(ctx, 10, 5);
  g.rr(ctx, bx0 + bw - 6, -4, w - bw + 6, 8, 4); ctx.fillStyle = '#C8793A'; ctx.fill();
  g.rr(ctx, bx0, -bh / 2, bw, bh, bh / 2.2); ctx.fillStyle = g.lg(ctx, -bh / 2, bh / 2, '#FFE2B0', '#E8A55E'); ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.lineJoin = 'round'; ctx.stroke();
  g.rr(ctx, bx0 + bw - 6, -4, w - bw + 6, 8, 4); ctx.stroke();
  ctx.beginPath(); ctx.arc(w / 2 - 8, 0, 2.5, 0, 7); ctx.fillStyle = INK; ctx.fill();
  ctx.strokeStyle = 'rgba(160,90,30,.35)'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(bx0 + 14, -2); ctx.bezierCurveTo(bx0 + bw * 0.4, -6, bx0 + bw * 0.6, 4, bx0 + bw - 14, 0); ctx.stroke();
  ctx.fillStyle = 'rgba(255,255,255,.55)'; g.rr(ctx, bx0 + 10, -bh / 2 + 3, bw - 30, 4, 2); ctx.fill();
  if (sel) { g.rr(ctx, -w / 2 - 6, -bh / 2 - 6, w + 12, bh + 12, 14); ctx.lineWidth = 4; ctx.strokeStyle = '#fff'; ctx.stroke(); }
}

// Intro animation: motor → wheel → belt with tomato
function introGears(ctx: Ctx2D, t: number, g: Gfx, big: boolean) {
  ctx.fillStyle = '#FFF4E6'; ctx.fillRect(0, 0, 460, 280);
  const w = W0 * 1.3, tt = t, m: Pt = [110, 110], p = big ? at(m, RS, RB, 20) : at(m, RS, RS, 20), q = big ? at(p, RB, RS, -20) : at(p, RS, RS, -20);
  const show = t > 40;
  motorBox(ctx, g, m[0], m[1], true, t);
  drawGear(ctx, g, m[0], m[1], NS, RS, tt * w, 'm');
  turnArrow(ctx, m[0], m[1], 21, 1, '#fff', 5);
  const r2 = big ? RB : RS, n2 = big ? NB : NS;
  const w2 = show ? -w * RS / r2 : 0;
  const ph2 = Math.atan2(p[1] - m[1], p[0] - m[0]) + Math.PI - (0.5 - ((Math.atan2(p[1] - m[1], p[0] - m[0])) * NS / (2 * Math.PI))) * 2 * Math.PI / n2;
  if (show) {
    const k = Math.min(1, (t - 40) / 20);
    drawGear(ctx, g, p[0], p[1] - (1 - k) * 60, n2, r2, ph2 + w2 * (t - 40), big ? 'b' : 's', { alpha: k });
    if (k >= 1) turnArrow(ctx, p[0], p[1], big ? 30 : 21, -1, '#fff', 5);
  } else drawPeg(ctx, g, p[0], p[1], t, true);
  const w3 = show && t > 60 ? w * RS / RS : 0;
  drawGear(ctx, g, q[0], q[1], NS, RS, w3 * (t - 60), 'c');
  const vb = w3 ? 3 : 0;
  const pos = t > 60 ? (t - 60) * vb : 0;
  drawBelt(ctx, g, q[0], 420, q[1], pos, vb);
  const bx = Math.min(q[0] + 60 + pos, 460), by = q[1] - RP - 16;
  tomato(ctx, g, bx, by, 15, pos / 15, { x: vb, y: 0 }, false);
}

// ---------- skins ----------
const skins: WorldDef['skins'] = {
  pizza_steel: { c1: '#E6E2F0', c2: '#A29BB8', r: 8 },
  pizza_table: { c1: '#F7C27E', c2: '#D9803E', r: 8 },
  // brick wall
  pizza_wall(ctx: Ctx2D, b: GameBody, g: Gfx) {
    const { x, y } = b.position, dw = b.dw!, dh = b.dh!;
    g.block(ctx, x, y, dw, dh, b.angle, '#F29384', '#C8574F', 8);
    ctx.save(); ctx.translate(x, y); ctx.rotate(b.angle);
    g.rr(ctx, -dw / 2, -dh / 2, dw, dh, 8); ctx.clip();
    ctx.strokeStyle = 'rgba(140,50,50,.35)'; ctx.lineWidth = 3;
    for (let yy = -dh / 2 + 16; yy < dh / 2; yy += 16) {
      ctx.beginPath(); ctx.moveTo(-dw / 2, yy); ctx.lineTo(dw / 2, yy); ctx.stroke();
    }
    for (let yy = -dh / 2, r = 0; yy < dh / 2; yy += 16, r++) for (let xx = -dw / 2 + (r % 2) * 18 + 18; xx < dw / 2; xx += 36) { ctx.beginPath(); ctx.moveTo(xx, yy); ctx.lineTo(xx, yy + 16); ctx.stroke(); }
    ctx.restore();
  },
};

// ---------- parts ----------
// The game's state in the build phase (browser only), so a dragged gear can snap to a peg
interface DevG { level?: Level; pl: Placement[]; S?: SimState }
const MK = (): DevG | null => (typeof window !== 'undefined' && window.__MK && window.__MK.G) || null;

function gearPart(size: Size): PartDef {
  const spec = GSPEC[size], col = size;
  return {
    gear: size, circle: spec.R, rot: false,
    draw(ctx, pl, sel, g) {
      // snap onto the nearest free peg (also while dragging – like a magnet)
      const G = MK();
      let x = pl.x, y = pl.y, ang = 0, snapped = false;
      if (G && G.level) {
        const i = G.pl.indexOf(pl);
        if (i >= 0) {
          const pegs = levelPegs(G.level), sn = snapList(pegs, G.pl)[i];
          if (sn != null) { x = pl.x = pegs[sn].x; y = pl.y = pegs[sn].y; snapped = true; }
          const rec = G.S && G.S.pz && G.S.pz.placed.find(r => r.idx === i);
          if (rec && rec.axle && rec.axle.x === x && rec.axle.y === y) ang = rec.axle.phase;
        } else if (G.level.solution && G.level.solution.includes(pl)) snapped = true;
      }
      drawGear(ctx, g, x, y, spec.n, spec.R, ang, col, { sel, alpha: snapped || !G ? 1 : 0.8 });
    },
    icon(ctx, g) { drawGear(ctx, g, 0, 0, spec.n, spec.R, 0.15, col, {}); },
    create(api, pl, info) {
      const Z = zz(api);
      const rec: PlacedGear = { pl, idx: info.idx, size, axle: null, x: pl.x, y: pl.y };
      Z.placed.push(rec);
      return {
        layer: 'back',
        draw(ctx, t, g, S) {
          if (rec.axle) drawAxleGears(ctx, g, rec.axle, S);
          else drawGear(ctx, g, rec.x, rec.y, spec.n, spec.R, 0, col, { alpha: 0.8 });
        },
      };
    },
  };
}

const parts = {
  pizza_wheel: definePart({ ...gearPart('s'), intro: (ctx, t, g) => introGears(ctx, t, g, false) }),
  pizza_big_wheel: definePart({ ...gearPart('b'), intro: (ctx, t, g) => introGears(ctx, t, g, true) }),
};

// ---------- objects ----------
interface GearState { ax: Axle; lastClick: number }
// Fixed gear (motor: 1 = clockwise, -1 = anticlockwise, 0 = loose wheel). s: 's' | 'b'
const gearObj = defineObj<GearState>({
  layer: 'back',
  create(api, od) {
    const o = od as GearObj, Z = zz(api);
    const ax = addAxle(Z, { x: o.x, y: o.y, gears: gearsOf(o.s || 's', o.motor ? 'm' : 'f'), motor: o.motor || 0, speed: o.speed, phase0: o.ph || 0 });
    return { ax, lastClick: 0 };
  },
  start(api, st) {
    const o = st.o as GearObj;
    if (o.motor) api.sfx('pizza_whirr', o.x, o.y);
    if (o.motor && zz(api).axles.some(a => a.jam)) api.sfx('pizza_clonk', o.x, o.y, '#FF5A5F', 6);
  },
  step(api, st) {
    const o = st.o as GearObj;
    if (o.motor && !st.ax.jam && api.S.t % 36 === 1) api.sfx('pizza_click', o.x, o.y);
  },
  blockers(o) { return (o as GearObj).motor ? [{ x: o.x, y: o.y + 40, r: 36 }] : []; },
  draw(ctx, st, t, g, S) {
    const o = st.o as GearObj, ax = st.ax;
    if (o.motor) motorBox(ctx, g, o.x, o.y, S.started && !ax.jam, S.t);
    drawAxleGears(ctx, g, ax, S);
    if (o.motor) turnArrow(ctx, o.x, o.y, ax.gears[0].R * 0.55, o.motor, '#fff', 6);
  },
});

const objs = {
  // Pegs on the wall where gears can go: { t: 'pizza_pegs', p: [[x, y], ...] }
  pizza_pegs: defineObj<{ axes: Axle[] }>({
    layer: 'back',
    create(api, od) {
      const o = od as PegsObj, Z = zz(api);
      // pegIdx = place in levelPegs(level), from this object's first index
      const idx = pegIndex(api.level, o);
      const axes = o.p.map((p, i) => addAxle(Z, { x: p[0], y: p[1], isPeg: true, pegIdx: idx + i }));
      return { axes };
    },
    draw(ctx, st, t, g, S) { for (const a of st.axes) if (!a.gears.length) drawPeg(ctx, g, a.x, a.y, t, !S.started); },
  }),
  pizza_gear: gearObj,
  // Conveyor belt: { t: 'pizza_belt', x1, x2, y, drive: 'L'|'R', gear: 's'|'b'|'sb'|null, k }
  pizza_belt: defineObj<BeltState>({
    layer: 'mid',
    create(api, od) {
      const o = od as BeltObj, Z = zz(api);
      const body = api.add(api.R((o.x1 + o.x2) / 2, o.y, o.x2 - o.x1 + 2 * RP, 2 * RP, { isStatic: true, friction: 0, restitution: 0.05, chamfer: { radius: RP - 1 }, label: 'pizza_belt' }));
      body.kind = 'pizza_belt';
      const ax0 = o.drive === 'R' ? o.x2 : o.x1;
      const pegIdx = o.gear ? null : pegIndex(api.level, o);
      const axle = addAxle(Z, { x: ax0, y: o.y, gears: gearsOf(o.gear, 'c'), isPeg: !o.gear, pegIdx });
      let axle2: Axle | null = null;
      if (o.gear2) { axle2 = addAxle(Z, { x: o.drive === 'R' ? o.x1 : o.x2, y: o.y, gears: gearsOf(o.gear2, 'c'), phase0: 0.3 }); Z.links.push([axle, axle2]); }
      const st: BeltState = { body, axle, axle2, pos: 0, vb: 0 };
      Z.belts.push(st);
      return st;
    },
    step(api, st) {
      const o = st.o as BeltObj;
      st.vb = st.axle.w * RP * BELTK * (o.k || 1);
      st.pos += st.vb;
      for (const b of api.touching(st.body)) if (api.isDynamic(b)) {
        const tv = b.position.y < o.y ? st.vb : -st.vb;
        api.Body.setVelocity(b, { x: b.velocity.x + (tv - b.velocity.x) * 0.3, y: b.velocity.y });
      }
    },
    draw(ctx, st, t, g, S) {
      const o = st.o as BeltObj;
      for (const ax of [st.axle, st.axle2]) if (ax && ax.gears.length && !ax.placed) {
        // the belt's own gear (behind the belt)
        const a = axleAngle(ax, S);
        ax.gears.slice().sort((p, q) => q.R - p.R).forEach((ge, i) => drawGear(ctx, g, ax.x, ax.y, ge.n, ge.R, a, ge.col, { noShadow: i > 0 }));
        if (ax.jam && S.started) jamMark(ctx, ax.x, ax.y - ax.gears[0].R - 6, S.t);
      }
      drawBelt(ctx, g, o.x1, o.x2, o.y, st.pos, st.vb);
      if (!st.axle.gears.length) drawPeg(ctx, g, st.axle.x, st.axle.y, t, !S.started);
    },
  }),
  // Dough roller: { t: 'pizza_roller', x, y, r, gear: 's'|'b'|null, k }
  pizza_roller: defineObj<RollerState>({
    layer: 'mid',
    create(api, od) {
      const o = od as RollerObj, Z = zz(api), r = o.r || 28;
      const body = api.add(api.Bodies.circle(o.x, o.y, r, { isStatic: true, friction: 0, restitution: 0.1, label: 'pizza_roller' }));
      body.kind = 'pizza_roller';
      const pegIdx = o.gear ? null : pegIndex(api.level, o);
      const axle = addAxle(Z, { x: o.x, y: o.y, gears: gearsOf(o.gear, 'c'), isPeg: !o.gear, pegIdx });
      const st: RollerState = { body, axle, r, ang: 0 };
      Z.pins.push(st);
      return st;
    },
    step(api, st) {
      const w = st.axle.w; st.ang += w * PINK * 2;
      if (!w) return;
      const sv = w * st.r * PINK * BELTK * ((st.o as RollerObj).k || 1), c = st.body.position;
      for (const b of api.touching(st.body)) if (api.isDynamic(b)) {
        const dx = b.position.x - c.x, dy = b.position.y - c.y, d = Math.hypot(dx, dy) || 1;
        const nx = dx / d, ny = dy / d, tx = -ny, ty = nx;
        const v = b.velocity; let vt = v.x * tx + v.y * ty, vn = v.x * nx + v.y * ny;
        vt += (sv - vt) * 0.45;
        if (sv * ty < 0 && ny > -0.8) vn = Math.min(vn, -0.6);   // the roller "grabs" the dough/tomato on the side that goes up
        api.Body.setVelocity(b, { x: tx * vt + nx * vn, y: ty * vt + ny * vn });
      }
    },
    draw(ctx, st, t, g, S) {
      const o = st.o;
      if (st.axle.gears.length && !st.axle.placed) {
        const a = axleAngle(st.axle, S);
        st.axle.gears.forEach(ge => drawGear(ctx, g, o.x, o.y, ge.n, ge.R, a, ge.col, {}));
        if (st.axle.jam && S.started) jamMark(ctx, o.x, o.y - st.axle.gears[0].R - 6, S.t);
      }
      drawRoller(ctx, g, o.x, o.y, st.r, st.ang);
      if (S.started && st.axle.w) turnArrow(ctx, o.x, o.y, st.r * 0.62, Math.sign(st.axle.w), '#fff', 5);
      if (!st.axle.gears.length) drawPeg(ctx, g, o.x, o.y, t, !S.started);
    },
  }),
  // Same as pizza_gear, but drawn in front of the player's wheels (for double gears)
  pizza_gearfront: defineObj<GearState>({ ...gearObj, layer: 'mid' }),
};

// ---------- sounds ----------
const sounds: Record<string, SoundFn> = {
  pizza_click: (tone) => { tone(900, 0.04, 'square', 0.08, 700); tone(1200, 0.03, 'square', 0.06, 900, 0.07); },
  pizza_whirr: (tone) => { tone(90, 0.5, 'sawtooth', 0.12, 140); tone(180, 0.4, 'triangle', 0.08, 260, 0.05); },
  pizza_clonk: (tone, noise) => { noise(0.18, 0.25, 500, 3); tone(140, 0.25, 'square', 0.12, 70); },
};

// ---------- reskins (kit parts, balls and goals in pizza style) ----------
const reskin: Partial<Record<CorePartType, Reskin>> = {
  // The plank is a pizza peel
  plank(ctx, pl, sel, g, rects) {
    const r = rects[0];
    ctx.save(); ctx.translate(r.x, r.y); ctx.rotate(r.a);
    peel(ctx, g, r.w, r.h, sel);
    ctx.restore();
  },
  // The pad is a soft lump of dough
  pad(ctx, pl, sel, g, rects) {
    const r = rects[0];
    ctx.save(); ctx.translate(r.x, r.y); ctx.rotate(r.a);
    g.shadowOn(ctx, 10, 5);
    ctx.beginPath(); ctx.moveTo(-r.w / 2, r.h / 2 + 4);
    ctx.bezierCurveTo(-r.w / 2 - 4, -r.h / 2 - 6, -r.w / 4, -r.h / 2 - 12, 0, -r.h / 2 - 8);
    ctx.bezierCurveTo(r.w / 4, -r.h / 2 - 12, r.w / 2 + 4, -r.h / 2 - 6, r.w / 2, r.h / 2 + 4);
    ctx.closePath(); ctx.fillStyle = g.lg(ctx, -r.h, r.h, '#FFF6E0', '#F0D49C'); ctx.fill(); g.shadowOff(ctx);
    ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
    ctx.fillStyle = 'rgba(255,255,255,.95)';
    [[-30, -6, 3], [-8, -12, 2.5], [18, -8, 3], [36, -2, 2]].forEach(([a, b, s]) => { ctx.beginPath(); ctx.arc(a, b, s, 0, 7); ctx.fill(); });
    if (sel) { g.rr(ctx, -r.w / 2 - 6, -r.h / 2 - 16, r.w + 12, r.h + 26, 12); ctx.lineWidth = 4; ctx.strokeStyle = '#fff'; ctx.stroke(); }
    ctx.restore();
  },
};
const icons: WorldDef['icons'] = {
  plank(ctx, g) { ctx.save(); ctx.rotate(-0.35); ctx.scale(0.6, 0.6); peel(ctx, g, 170, 18, false); ctx.restore(); },
  pad(ctx, g) {
    ctx.beginPath(); ctx.moveTo(-44, 14); ctx.bezierCurveTo(-48, -14, -20, -26, 0, -22); ctx.bezierCurveTo(20, -26, 48, -14, 44, 14); ctx.closePath();
    ctx.fillStyle = g.lg(ctx, -24, 14, '#FFF6E0', '#F0D49C'); ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  },
};
type GoalPos = { x: number; y: number; w: number };
const goal = {
  // The pizza box
  bowl(ctx: Ctx2D, gl: GoalPos, full: number, t: number, g: Gfx) {
    const { x, y, w } = gl;
    ctx.save();
    // lid (open, tipped back)
    ctx.beginPath(); ctx.moveTo(x - w / 2 - 2, y - 56); ctx.lineTo(x + w / 2 + 2, y - 56); ctx.lineTo(x + w / 2 - 12, y - 126); ctx.lineTo(x - w / 2 + 12, y - 126); ctx.closePath();
    g.shadowOn(ctx, 10, 4); ctx.fillStyle = g.lg(ctx, y - 126, y - 56, '#F7D29C', '#E3A968'); ctx.fill(); g.shadowOff(ctx);
    ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.lineJoin = 'round'; ctx.stroke();
    pizzaTop(ctx, x, y - 92, 26, 22, 0);
    // the box
    ctx.beginPath(); ctx.moveTo(x - w / 2 - 6, y - 58); ctx.lineTo(x + w / 2 + 6, y - 58); ctx.lineTo(x + w / 2 - 2, y + 8); ctx.lineTo(x - w / 2 + 2, y + 8); ctx.closePath();
    g.shadowOn(ctx, 14, 7); ctx.fillStyle = g.lg(ctx, y - 58, y + 8, '#F2C58A', '#D9954E'); ctx.fill(); g.shadowOff(ctx);
    ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
    ctx.strokeStyle = 'rgba(140,80,30,.35)'; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(x - w / 2 + 8, y - 20); ctx.lineTo(x + w / 2 - 8, y - 20); ctx.stroke();
    // the opening with the pizza
    ctx.beginPath(); ctx.ellipse(x, y - 58, w / 2 + 6, 10, 0, 0, 7); ctx.fillStyle = '#B87A3E'; ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
    pizzaTop(ctx, x, y - 58, w / 2 - 6, 7, full);
    ctx.restore();
  },
  front(ctx: Ctx2D, gl: GoalPos) {
    const { x, y, w } = gl;
    ctx.save(); ctx.beginPath(); ctx.ellipse(x, y - 58, w / 2 + 6, 10, 0, 0, Math.PI); ctx.lineWidth = 5; ctx.strokeStyle = '#F2C58A'; ctx.stroke(); ctx.restore();
  },
  // The pizza oven (bell goal)
  bell(ctx: Ctx2D, gl: { x: number; y: number; hitT?: number }, t: number, g: Gfx) {
    const { x, y } = gl, hit = gl.hitT != null;
    ctx.save();
    g.shadowOn(ctx, 14, 7);
    ctx.beginPath(); ctx.moveTo(x - 70, y + 40); ctx.lineTo(x - 70, y - 10); ctx.quadraticCurveTo(x - 70, y - 80, x, y - 80); ctx.quadraticCurveTo(x + 70, y - 80, x + 70, y - 10); ctx.lineTo(x + 70, y + 40); ctx.closePath();
    ctx.fillStyle = g.lg(ctx, y - 80, y + 40, '#F29384', '#C8574F'); ctx.fill(); g.shadowOff(ctx);
    ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
    ctx.strokeStyle = 'rgba(140,50,50,.4)'; ctx.lineWidth = 3;
    for (let yy = y - 60; yy < y + 40; yy += 18) { ctx.beginPath(); ctx.moveTo(x - 66, yy); ctx.lineTo(x + 66, yy); ctx.stroke(); }
    // opening with fire
    ctx.beginPath(); ctx.moveTo(x - 38, y + 40); ctx.lineTo(x - 38, y + 4); ctx.quadraticCurveTo(x - 38, y - 38, x, y - 38); ctx.quadraticCurveTo(x + 38, y - 38, x + 38, y + 4); ctx.lineTo(x + 38, y + 40); ctx.closePath();
    ctx.fillStyle = INK; ctx.fill();
    const f = hit ? 1.4 : 1;
    ctx.fillStyle = '#FF9F3C';
    ctx.beginPath(); ctx.moveTo(x - 30, y + 38);
    for (let i = 0; i <= 6; i++) { const xx = x - 30 + i * 10, hh = (16 + ((i * 7) % 11) + Math.sin(t * 0.2 + i) * 5) * f; ctx.lineTo(xx, y + 38 - hh); ctx.lineTo(xx + 5, y + 38 - hh * 0.5); }
    ctx.lineTo(x + 30, y + 38); ctx.closePath(); ctx.fill();
    ctx.fillStyle = '#FFE36B'; ctx.beginPath(); ctx.ellipse(x, y + 32, 18, 8 * f, 0, 0, 7); ctx.fill();
    // chimney
    g.block(ctx, x, y - 92, 36, 26, 0, '#C9C3D6', '#8B84A0', 6);
    ctx.restore();
  },
};

// ---------- meta ----------
const synth: SynthStyle = { bpm: 82, root: 261.6, sc: 'major', prog: [0, 3, 4, 0], bar: 3, lead: 'pluck', dens: 0.45 };
const meta: WorldMeta = {
  icon: '🍕', strings, music: 'pizza.mp3', synth,
  credits: { title: 'Italian Cafe Music', artist: 'andriih', id: 567456, url: 'https://pixabay.com/music/orchestral-italian-cafe-music-567456/' },
};

export default defineWorld({
  k: K,
  meta,
  scene,
  wash: 'rgba(255,244,232,.6)',
  bgExtra(ctx, W) {
    // flour on the floor
    ctx.fillStyle = 'rgba(255,255,255,.35)';
    for (let i = 0; i < 14; i++) { ctx.beginPath(); ctx.ellipse((i * 173) % W, 770 + (i % 3) * 8, 40 + (i % 4) * 10, 6, 0, 0, 7); ctx.fill(); }
  },
  skins,
  ball(ctx, b, kind, t, g) {
    tomato(ctx, g, b.position.x, b.position.y, b.circleRadius!, b.roll, b.velocity || { x: 0, y: 0 }, b.sad, kind === 'helper');
  },
  goal,
  reskin,
  icons,
  sounds,
  // All gears are created – find the pegs, snap the wheels on, and work out what turns
  setup(api, level) {
    const Z = zz(api);
    const pegAx = Z.axles.filter(a => a.isPeg);
    // pegAx is in the same order as levelPegs(level)
    const pegs = levelPegs(level);
    const pls: Placement[] = []; Z.placed.forEach(r => { pls[r.idx] = r.pl; });
    const sn = snapList(pegs, pls);
    for (const r of Z.placed) {
      const j = sn[r.idx];
      if (j == null) continue;
      const ax = pegAx.find(a => a.pegIdx === j);
      if (!ax) continue;
      ax.gears.push({ n: GSPEC[r.size].n, R: GSPEC[r.size].R, col: r.size });
      ax.placed = r; r.axle = ax;
    }
    solve(Z);
  },
  parts,
  objs,
  levels,
});

